import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { ReviewsState, ReviewsStatus } from '@spike/reviews-action';
import useApiClientWrapper from 'hooks/useApiClientWrapper';
import { fetchReviewSettingsThunk, saveReviewSettingsThunk } from '@spike/reviews-action';
import { Drawer } from 'components/UI/V3/Drawer/Drawer';
import { DrawerHeader } from 'components/UI/V3/Drawer/Header/DrawerHeader';
import { DrawerBody } from 'components/UI/V3/Drawer/Body/DrawerBody';
import { DrawerFooter } from 'components/UI/V3/Drawer/Footer/DrawerFooter';
import { Switch } from 'components/UI/V3/Forms/Switch/Switch';
import { Input } from 'components/UI/V3/Forms/Input/Input';
import Button from 'components/UI/V3/Button/Button';
import styles from './SetupReviewsDrawer.module.scss';
import Spinner from 'components/UI/V3/Spinner';

export interface SetupReviewsDrawerProps {
    open: boolean;
    onClose: () => void;
}

type Provider = 'google' | 'facebook' | 'yelp' | 'trustpilot';
type ProviderConfig = { enabled: boolean; url: string };
type ProvidersState = Record<Provider, ProviderConfig>;

const PROVIDERS: Provider[] = ['google', 'facebook', 'yelp', 'trustpilot'];

export const SetupReviewsDrawer: React.FC<SetupReviewsDrawerProps> = ({ open, onClose }) => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper() as any;

    // Local-only error fallback; primary loading/saving/error come from Redux state
    const [error, setError] = React.useState<string | null>(null);
    const [acceptReviews, setAcceptReviews] = React.useState<boolean>(true);
    const [providers, setProviders] = React.useState<ProvidersState>({
        google: { enabled: true, url: '' },
        facebook: { enabled: false, url: '' },
        yelp: { enabled: false, url: '' },
        trustpilot: { enabled: false, url: '' }
    });

    const setProvider = React.useCallback((key: Provider, data: Partial<ProviderConfig>) => {
        setProviders(prev => ({ ...prev, [key]: { ...prev[key], ...data } }));
    }, []);

    const toLocal = (api: any) => {
        const acceptNew = api?.reviewsEnabled;
        const channels: Array<any> | undefined = api?.reviewChannels ?? api?.channels;
        const acceptLegacy = Boolean(api?.enabled);

        const mapFromChannels = (): ProvidersState | undefined => {
            if (!Array.isArray(channels)) return undefined;
            const base: ProvidersState = {
                google: { enabled: false, url: '' },
                facebook: { enabled: false, url: '' },
                yelp: { enabled: false, url: '' },
                trustpilot: { enabled: false, url: '' }
            };
            for (const ch of channels) {
                const prov = String(ch?.provider ?? '').toLowerCase();
                const p = prov as Provider;
                if (PROVIDERS.includes(p)) {
                    base[p] = {
                        enabled: Boolean(ch.enabled),
                        url: String(ch.url || '')
                    };
                }
            }
            return base;
        };

        const getProvLegacy = (k: Provider): ProviderConfig => ({
            enabled: Boolean(api?.providers?.[k]?.enabled ?? api?.enabledProviders?.includes?.(k)),
            url: String(api?.providers?.[k]?.url ?? api?.links?.[`${k}Url`] ?? api?.[`${k}Url`] ?? '')
        });

        const providersNew = mapFromChannels();
        return {
            acceptReviews: acceptNew ?? acceptLegacy,
            providers: providersNew ?? {
                google: getProvLegacy('google'),
                facebook: getProvLegacy('facebook'),
                yelp: getProvLegacy('yelp'),
                trustpilot: getProvLegacy('trustpilot')
            }
        };
    };

    const toApi = () => {
        const reviewChannels = PROVIDERS.map(provider => ({
            provider,
            enabled: providers[provider].enabled,
            url: providers[provider].url || null
        }));
        return {
            reviewsEnabled: acceptReviews,
            reviewChannels
        };
    };

    // Derive settings state from Redux (reviews slice)
    const [hasInitialized, setHasInitialized] = React.useState(false);
    const reviewsSlice = useSelector<RootState, ReviewsState | undefined>(s => (s as any).reviews);
    const saving: boolean = reviewsSlice?.status === ReviewsStatus.SettingsSaving;
    const settingsError: string | null = null; // Library state does not expose a dedicated settings error string
    const settingsData: any = reviewsSlice?.settings ?? null;
    const status = useSelector<RootState, ReviewsStatus>(state => state.reviews.status);
    const uiLoading = useSelector<RootState, boolean | undefined>(state => state.reviews.settingsLoading);

    // Load settings when opening
    React.useEffect(() => {
        if (!open) return;
        setError(null);
        setHasInitialized(false);

        try {
            dispatch(fetchReviewSettingsThunk(apiClientWrapper));
        } catch (e) {
            setError('Failed to load settings');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    React.useEffect(() => {
        if (status === ReviewsStatus.SettingsFetchSuccess && settingsData) {
            try {
                const local = toLocal(settingsData);
                setAcceptReviews(local.acceptReviews);
                setProviders(local.providers);
                setHasInitialized(true);
            } catch (e) {
                // Ignorar errores de mapeo, dejar valores por defecto.
            }
        }

        if (status === ReviewsStatus.Error) {
            setError('Failed to load settings');
            setHasInitialized(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status, settingsData]);

    const handleSave = async () => {
        setError(null);
        try {
            const payload = toApi() as any; // Cast to lib model (SaveReviewSettingsModel)
            await (dispatch as any)(saveReviewSettingsThunk(apiClientWrapper, payload));
            onClose();
        } catch (e) {
            setError('Failed to save settings');
        }
    };

    return (
        <Drawer onClose={onClose}>
            <DrawerHeader title="Setup Reviews" />
            <DrawerBody>
                <div className={styles.root}>
                    <div className={styles.section}>
                        {uiLoading || !hasInitialized ? (
                            <div className={styles.spinnerContainer}>
                                <Spinner />
                            </div>
                        ) : (
                            <>
                                {(settingsError || error) && (
                                    <div
                                        className={styles.row}
                                        style={{ color: '#c62828' }}
                                    >
                                        {settingsError || error}
                                    </div>
                                )}
                                <div className={styles.row}>
                                    <Switch
                                        inlineLabel="Accept Client Reviews"
                                        checked={acceptReviews}
                                        onChange={setAcceptReviews}
                                        disabled={uiLoading || saving}
                                    />
                                    <div className={styles.description}>
                                        Enable or disable collecting client reviews.
                                    </div>
                                </div>

                                <div className={styles.row}>
                                    <div className={styles.sectionTitle}>Connect Your Accounts</div>
                                    <div className={styles.description}>Select a color to identify this service.</div>
                                </div>

                                {/* Google */}
                                <div className={styles.row}>
                                    <Switch
                                        inlineLabel="Google"
                                        checked={providers.google.enabled}
                                        onChange={checked => setProvider('google', { enabled: checked })}
                                        disabled={uiLoading || saving}
                                    />
                                    <div className={styles.urlField}>
                                        <Input
                                            id="google-review-url"
                                            aria-label="Google Review URL"
                                            placeholder="Ex. https://search.google.com/local/writereview?placeid=..."
                                            value={providers.google.url}
                                            disabled={!providers.google.enabled || uiLoading || saving}
                                            onChange={e => setProvider('google', { url: e.target.value })}
                                        />
                                    </div>
                                </div>

                                {/* Facebook */}
                                <div className={styles.row}>
                                    <Switch
                                        inlineLabel="Facebook"
                                        checked={providers.facebook.enabled}
                                        onChange={checked => setProvider('facebook', { enabled: checked })}
                                        disabled={uiLoading || saving}
                                    />
                                    <div className={styles.urlField}>
                                        <Input
                                            id="facebook-review-url"
                                            aria-label="Facebook Review URL"
                                            placeholder="Ex. https://www.facebook.com/<page>/reviews/"
                                            value={providers.facebook.url}
                                            disabled={!providers.facebook.enabled || uiLoading || saving}
                                            onChange={e => setProvider('facebook', { url: e.target.value })}
                                        />
                                    </div>
                                </div>

                                {/* Yelp */}
                                <div className={styles.row}>
                                    <Switch
                                        inlineLabel="Yelp"
                                        checked={providers.yelp.enabled}
                                        onChange={checked => setProvider('yelp', { enabled: checked })}
                                        disabled={uiLoading || saving}
                                    />
                                    <div className={styles.urlField}>
                                        <Input
                                            id="yelp-review-url"
                                            aria-label="Yelp Review URL"
                                            placeholder="Ex. https://www.yelp.com/writereview/biz/..."
                                            value={providers.yelp.url}
                                            disabled={!providers.yelp.enabled || uiLoading || saving}
                                            onChange={e => setProvider('yelp', { url: e.target.value })}
                                        />
                                    </div>
                                </div>

                                {/* Trustpilot */}
                                <div className={styles.row}>
                                    <Switch
                                        inlineLabel="Trustpilot"
                                        checked={providers.trustpilot.enabled}
                                        onChange={checked => setProvider('trustpilot', { enabled: checked })}
                                        disabled={uiLoading || saving}
                                    />
                                    <div className={styles.urlField}>
                                        <Input
                                            id="trustpilot-review-url"
                                            aria-label="Trustpilot Review URL"
                                            placeholder="Ex. https://www.trustpilot.com/evaluate/<domain>"
                                            value={providers.trustpilot.url}
                                            disabled={!providers.trustpilot.enabled || uiLoading || saving}
                                            onChange={e => setProvider('trustpilot', { url: e.target.value })}
                                        />
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </DrawerBody>

            <DrawerFooter className={styles.footer}>
                <Button
                    label={saving ? 'Saving...' : 'Save'}
                    onClick={handleSave}
                    disabled={uiLoading || saving}
                    variant="green"
                    size="large"
                />
            </DrawerFooter>
        </Drawer>
    );
};

export default SetupReviewsDrawer;
